import { HostListener } from '@rhtml/decorators';
import { Inject } from '@rxdi/core';
import { Component, css, html, LitElement, state } from '@rxdi/lit-html';
import { Router } from '@rxdi/router';
import { ScrollToService } from '@shared/services/scroll/scroll-to.service';
import { ArrowIcon, StudioStyles } from '@shared/styles/studio.styles';
import { lastValueFrom } from 'rxjs';
import { delay, take, tap } from 'rxjs/operators';

import { Logo } from './logo';

interface NavLink {
  label: string;
  section?: string;
  route?: string;
}

const LINKS: NavLink[] = [
  { label: 'Work', section: 'rs-work' },
  { label: 'Stack', section: 'rs-stack' },
  { label: 'Services', section: 'rs-services' },
  { label: 'Open source', section: 'rs-open-source' },
  { label: 'Journal', route: '/blog' },
];

/** Site header: brand, section links, call to action, mobile menu. */
@Component<NavbarComponent>({
  selector: 'navbar-component',
  styles: [
    StudioStyles,
    css`
      :host {
        /* the logo paints with --primary-color */
        --primary-color: var(--rs-signal);
      }

      header {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 20;
        padding: 14px 0;
        background: var(--rs-surface-glass);
        backdrop-filter: blur(18px) saturate(1.4);
        -webkit-backdrop-filter: blur(18px) saturate(1.4);
        border-bottom: 1px solid var(--rs-line);
        transition: padding 0.35s var(--rs-ease);
      }

      header.scrolled {
        padding: 10px 0;
      }

      .bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 24px;
      }

      .brand {
        display: inline-flex;
        align-items: center;
        gap: 12px;
        cursor: pointer;
        background: none;
        border: none;
        padding: 0;
        color: var(--rs-text);
      }

      .brand svg {
        width: 30px;
        height: 34px;
      }

      .brand b {
        font-family: var(--rs-font-display);
        font-size: 19px;
        font-weight: 700;
        letter-spacing: -0.02em;
      }

      nav {
        display: none;
        align-items: center;
        gap: 4px;
        padding: 5px;
        border-radius: 999px;
        border: 1px solid var(--rs-line);
        background: var(--rs-surface-glass);
      }

      nav button {
        height: 38px;
        padding: 0 16px;
        border-radius: 999px;
        border: none;
        background: transparent;
        color: var(--rs-text-2);
        font: 500 14px var(--rs-font-body);
        cursor: pointer;
        transition: color 0.2s var(--rs-ease), background 0.2s var(--rs-ease);
      }

      nav button:hover {
        color: var(--rs-text);
        background: var(--rs-line);
      }

      .cta {
        display: none;
        height: 44px;
        padding: 0 20px;
      }

      .burger {
        display: inline-grid;
        place-items: center;
        width: 44px;
        height: 44px;
        border-radius: 50%;
        border: 1px solid var(--rs-line-strong);
        background: var(--rs-surface-glass);
        color: var(--rs-text);
        cursor: pointer;
      }

      .burger span,
      .burger span::before,
      .burger span::after {
        display: block;
        width: 16px;
        height: 1.5px;
        background: currentColor;
        position: relative;
        transition: transform 0.3s var(--rs-ease);
      }

      .burger span::before,
      .burger span::after {
        content: '';
        position: absolute;
      }
      .burger span::before {
        top: -5px;
      }
      .burger span::after {
        top: 5px;
      }

      .burger.open span {
        background: transparent;
      }
      .burger.open span::before {
        transform: translateY(5px) rotate(45deg);
      }
      .burger.open span::after {
        transform: translateY(-5px) rotate(-45deg);
      }

      @media (min-width: 980px) {
        nav,
        .cta {
          display: inline-flex;
        }
        .burger {
          display: none;
        }
      }

      .sheet {
        position: fixed;
        inset: 0;
        z-index: 19;
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 6px;
        padding: 96px var(--rs-gutter) 40px;
        background: var(--rs-ink);
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.3s var(--rs-ease);
      }

      .sheet.open {
        opacity: 1;
        pointer-events: auto;
      }

      .sheet button {
        text-align: left;
        background: none;
        border: none;
        padding: 8px 0;
        color: var(--rs-text);
        font-family: var(--rs-font-display);
        font-size: clamp(36px, 10vw, 56px);
        font-weight: 700;
        letter-spacing: -0.03em;
        cursor: pointer;
      }

      .sheet .btn {
        margin-top: 28px;
        width: fit-content;
      }

      @media (min-width: 980px) {
        .sheet {
          display: none;
        }
      }
    `,
  ],
  template(this: NavbarComponent) {
    return html`
      <header class=${this.scrolled ? 'scrolled' : ''}>
        <div class="wrap bar">
          <button class="brand" aria-label="Reactive Solutions, home" @click=${() => this.home()}>
            ${Logo}<b>Reactive Solutions</b>
          </button>
          <nav aria-label="Main">
            ${LINKS.map((l) => html`<button @click=${() => this.open(l)}>${l.label}</button>`)}
          </nav>
          <a class="btn btn-signal cta" href="#contact" @click=${(e: Event) => this.cta(e)}>
            Start a project ${ArrowIcon}
          </a>
          <button
            class="burger ${this.menu ? 'open' : ''}"
            aria-label="Menu"
            aria-expanded=${this.menu ? 'true' : 'false'}
            @click=${() => (this.menu = !this.menu)}
          >
            <span></span>
          </button>
        </div>
      </header>
      <div class="sheet ${this.menu ? 'open' : ''}" aria-hidden=${this.menu ? 'false' : 'true'}>
        ${LINKS.map((l) => html`<button @click=${() => this.open(l)}>${l.label}</button>`)}
        <a class="btn btn-signal" href="#contact" @click=${(e: Event) => this.cta(e)}>Start a project ${ArrowIcon}</a>
      </div>
    `;
  },
})
export class NavbarComponent extends LitElement {
  @state() scrolled = false;
  @state() menu = false;

  @Inject(ScrollToService)
  private scrollToService: ScrollToService;

  @Router() router: Router;

  @HostListener('window:scroll')
  onScroll() {
    const scrolled = window.scrollY > 40;
    if (scrolled !== this.scrolled) {
      this.scrolled = scrolled;
    }
  }

  private open(link: NavLink) {
    this.menu = false;
    if (link.route) {
      this.router.go(link.route);
      window.scrollTo({ top: 0 });
      return;
    }
    void this.scrollToSection(link.section);
  }

  private cta(event: Event) {
    event.preventDefault();
    this.menu = false;
    void this.scrollToSection('rs-contact');
  }

  private home() {
    this.menu = false;
    this.router.go('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private async scrollToSection(section: string) {
    if (this.router.activePath !== '/') {
      this.router.go('/');
      await lastValueFrom(
        this.router.onSnapshotChange().pipe(
          delay(50),
          take(1),
          tap(() => this.scrollToService.scrollToElement(section))
        )
      );
      return;
    }
    this.scrollToService.scrollToElement(section);
  }
}
